import React, { useState, useCallback, useMemo } from "react";
import { Button, Dropdown, Flex, Menu, Drawer, Tooltip } from "antd";
import { LogoutOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import EmojiIcon from "../../assets/icons/Emoji";
import { InfoCircleIcon } from "../../assets/icons";
import { API_ENDPOINTS } from "../../API/endpoints";
import { handleApiError } from "../../utils/CommonFunctions";
import ApiService from "../../API/apiService";

import { useUserSelfStore } from "../../store/apis/useUserStore";

import UserStatusBadge from "../../Components/UserStatusBadge";
import CustomAvatar from "../../Components/CustomAvatar";
import CustomUserStatusModal from "./modals/CustomUserStatusModal";
import UserProfileModal from "./modals/UserProfileModal";

const INITIAL_MODAL_STATE = {
  activeModal: "",
  modalPayload: "",
};

const STATUS_OPTIONS = [
  { key: "online", status: "Online" },
  { key: "away", status: "away" },
  { key: "busy", status: "busy" },
  { key: "do_not_disturb", status: "do_not_disturb" },
  { key: "offline", status: "offline" },
];

const ProfileMenu = ({ placement = "bottomRight", avatarSize = 28 }) => {
  const navigate = useNavigate();
  const [modalConfig, setModalConfig] = useState(INITIAL_MODAL_STATE);
  const [openProfileSidebarMenu, setOpenProfileSidebarMenu] = useState(false);

  const userSelfData = useUserSelfStore((state) => state.userSelfData);
  const getUserSelfDetails = useUserSelfStore(
    (store) => store.getUserSelfDetails
  );

  // Memoized callbacks
  const handleModalUpdate = useCallback((config) => {
    setModalConfig((prev) => ({ ...prev, ...config }));
  }, []);

  const showProfileModal = useCallback(() => {
    handleModalUpdate({ activeModal: "UserProfileModal" });
    setOpenProfileSidebarMenu(false);
  }, [handleModalUpdate]);

  const showSetCustomStatusModal = useCallback(
    (e) => {
      e.stopPropagation();
      setOpenProfileSidebarMenu(false);
      handleModalUpdate({ activeModal: "CustomUserStatusModal" });
    },
    [handleModalUpdate]
  );

  const onClose = useCallback(() => {
    setOpenProfileSidebarMenu(false);
  }, []);

  const showProfileSidebarMenu = useCallback(() => {
    setOpenProfileSidebarMenu(true);
  }, []);

  const logOut = useCallback(() => {
    localStorage.clear();
    navigate("/");
  }, [navigate]);

  // Optimized status update function
  const updateUserStatus = useCallback(
    async (newStatus) => {
      if (!userSelfData?._id) return;

      const reqBody = {
        status: newStatus,
        user_id: userSelfData._id,
        is_status: true,
        isAutomatic: false,
      };

      try {
        const response = await ApiService.post(
          API_ENDPOINTS.changestatus,
          reqBody
        );
        if (response?.data && response?.statusCode === 200) {
          const updatedUserDetails = { ...userSelfData, status: newStatus };
          getUserSelfDetails(updatedUserDetails._id);
          localStorage.setItem("userData", JSON.stringify(updatedUserDetails));
        }
      } catch (error) {
        handleApiError(error, "Error while updating status");
      }
    },
    [userSelfData, getUserSelfDetails]
  );

  const clearCustomStatus = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!userSelfData?._id) return;

      const reqBody = {
        custom_status: "",
        custom_status_emoji: "",
        user_id: userSelfData._id,
        is_custom_status: true,
      };

      try {
        const response = await ApiService.post(
          API_ENDPOINTS.changestatus,
          reqBody
        );
        if (response?.data && response?.statusCode === 200) {
          const updatedUserDetails = {
            ...userSelfData,
            custom_status: "",
            custom_status_emoji: "",
          };
          getUserSelfDetails(updatedUserDetails._id);
          localStorage.setItem("userData", JSON.stringify(updatedUserDetails));
        }
      } catch (error) {
        handleApiError(error, "Error while clearing custom status");
      }
    },
    [userSelfData, getUserSelfDetails]
  );

  const handleUserDetailsUpdate = useCallback(
    (data) => {
      getUserSelfDetails(data._id);
    },
    [getUserSelfDetails]
  );

  // Memoized menu items
  const profileMenuItems = useMemo(
    () => [
      {
        key: "profileMenuItem-1",
        label: (
          <Flex align="center" gap="small">
            <CustomAvatar
              img={userSelfData?.thumbnail_avatarUrl}
              userName={userSelfData?.username}
              size={40}
            />
            <Flex vertical gap="4px">
              <span className="name">{userSelfData?.fullName}</span>
              <span className="username">@{userSelfData?.username}</span>
            </Flex>
          </Flex>
        ),
        className: "profile-menu-dropdown__title",
      },
      { type: "divider" },
      {
        key: "profileMenuItem-2",
        label: (
          <Flex style={{ width: "100%" }} align="center" gap="small">
            {userSelfData?.custom_status ? (
              <div className="customtStatusMainContainier">
                <div
                  className="customStatusImgText"
                  onClick={showSetCustomStatusModal}
                >
                  {userSelfData.custom_status_emoji && (
                    <img
                      className="search-icon"
                      width={20}
                      height={20}
                      src={userSelfData.custom_status_emoji}
                      alt="custom status emoji"
                    />
                  )}
                  <p>{userSelfData.custom_status}</p>
                </div>
                <Button
                  type="text"
                  variant="filled"
                  onClick={clearCustomStatus}
                >
                  <Tooltip title="Clear">
                    <CloseCircleOutlined style={{ fontSize: "18px" }} />
                  </Tooltip>
                </Button>
              </div>
            ) : (
              <div
                onClick={showSetCustomStatusModal}
                className="customStatusImgText"
              >
                <EmojiIcon /> Set a Custom Status
              </div>
            )}
          </Flex>
        ),
      },
      { type: "divider" },
      ...STATUS_OPTIONS.map((statusOption, index) => ({
        key: `profileMenuItem-${index + 3}`,
        label: (
          <UserStatusBadge status={statusOption.status} showStatus={true} />
        ),
        className:
          statusOption.key === "busy" || statusOption.key === "do_not_disturb"
            ? "ec-dropdown__submenu profile-menu-dropdown__submenu--dnd"
            : undefined,
        onClick: () => updateUserStatus(statusOption.key),
      })),
      { type: "divider" },
      {
        key: "profileMenuItem-8",
        label: (
          <Flex align="center" gap="small">
            <i className="fa-regular fa-user"></i>
            Profile
          </Flex>
        ),
        onClick: showProfileModal,
      },
      { type: "divider" },
      {
        key: "profileMenuItem-9",
        label: (
          <Flex align="center" gap="small">
            <LogoutOutlined />
            Log Out
          </Flex>
        ),
        onClick: logOut,
      },
    ],
    [
      userSelfData,
      showSetCustomStatusModal,
      clearCustomStatus,
      updateUserStatus,
      showProfileModal,
      logOut,
    ]
  );

  const mobileDrawerProfileMenuItems = useMemo(
    () => [
      ...profileMenuItems.slice(0, -2),
      {
        key: "mobileDrawerProfileMenuItem-12",
        label: (
          <Flex align="center" gap="middle">
            <i className="fa-solid fa-gear"></i>
            <span>Log Out</span>
          </Flex>
        ),
        onClick: logOut,
      },
    ],
    [profileMenuItems, logOut]
  );

  return (
    <>
      <Flex gap="small" className="menu-controls-lg">
        <div className="profile-menu-dropdown-wrapper">
          <Dropdown
            menu={{ items: profileMenuItems }}
            className="ec-dropdown profile-menu-dropdown"
            placement={placement}
            popupRender={(menu) => (
              <div className="ec-dropdown__menu profile-menu-dropdown__menu">
                {menu}
              </div>
            )}
            trigger={["click"]}
          >
            <Button type="text">
              <div className="avatar-badge-wrapper">
                <CustomAvatar
                  size={avatarSize}
                  img={userSelfData?.thumbnail_avatarUrl}
                  userName={userSelfData?.username}
                />
                <UserStatusBadge
                  status={userSelfData?.status}
                  showStatus={false}
                  font_size="12px"
                  badgeBackground="#FFF"
                />
              </div>
            </Button>
          </Dropdown>
        </div>
      </Flex>

      <div className="menu-controls-sm">
        <Button type="text" className="info-btn" onClick={() => {}}>
          <InfoCircleIcon />
        </Button>
        <Button type="text" onClick={showProfileSidebarMenu}>
          <i className="fa-solid fa-bars"></i>
        </Button>
        <Drawer
          placement="right"
          closable={false}
          onClose={onClose}
          open={openProfileSidebarMenu}
          className="mobile-drawer-profile-menu"
          width="auto"
        >
          <Flex vertical className="profile-menu-sidebar">
            <Menu
              onClick={onClose}
              style={{ width: 256 }}
              items={mobileDrawerProfileMenuItems}
            />
          </Flex>
        </Drawer>
      </div>

      {modalConfig.activeModal === "CustomUserStatusModal" && (
        <CustomUserStatusModal
          onModalUpdate={handleModalUpdate}
          userSelfData={userSelfData}
          onUserDetailsUpdate={handleUserDetailsUpdate}
        />
      )}

      {modalConfig.activeModal === "UserProfileModal" && (
        <UserProfileModal
          onModalUpdate={handleModalUpdate}
          userSelfData={userSelfData}
        />
      )}
    </>
  );
};

export default ProfileMenu;
